import { useMemo } from 'react';
import { type AnimationConfig, defaultAnimationConfig } from '../types/animation';

interface UseAnimationConfigOptions {
  respectReducedMotion?: boolean;
  globalConfig?: Partial<AnimationConfig>;
}

export const useAnimationConfig = (
  config: Partial<AnimationConfig> = {},
  options: UseAnimationConfigOptions = {}
) => {
  const { respectReducedMotion = true, globalConfig = {} } = options;

  const finalConfig = useMemo(() => {
    // Check for reduced motion preference
    const prefersReducedMotion = respectReducedMotion && 
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const baseConfig = {
      ...defaultAnimationConfig,
      ...globalConfig,
      ...config
    };

    // If user prefers reduced motion, disable animations
    if (prefersReducedMotion) {
      return {
        ...baseConfig,
        duration: 0,
        delay: 0,
        pauseScroll: false,
        pauseDuration: 0
      };
    }

    return baseConfig;
  }, [config, globalConfig, respectReducedMotion]);

  return finalConfig;
};

// Utility function to create custom easing curves
export const createEasing = (
  x1: number, 
  y1: number, 
  x2: number, 
  y2: number
): string => {
  return `cubic-bezier(${x1}, ${y1}, ${x2}, ${y2})`;
};

// Predefined easing curves
export const easingCurves = {
  easeOut: [0.25, 0.46, 0.45, 0.94],
  easeIn: [0.55, 0.06, 0.68, 0.19],
  easeInOut: [0.42, 0, 0.58, 1],
  easeOutBack: [0.34, 1.56, 0.64, 1],
  easeInBack: [0.6, -0.28, 0.735, 0.045],
  easeOutElastic: [0.68, -0.55, 0.265, 1.55],
  smooth: [0.25, 0.1, 0.25, 1],
  snappy: [0.4, 0, 0.2, 1]
} as const;

export type EasingCurveName = keyof typeof easingCurves;